import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { RequestDetailsHeader } from "@/components/RequestDetailsHeader";
import { Loader } from "lucide-react";

interface Props {
  /** Page title shown in the header (self variant). */
  title?: string;
  /** Status chip value. */
  status?: string | null;
  /** Back navigation handler. */
  onBack: () => void;
  /** Whether data is loading. */
  isLoading?: boolean;
  /** Main content (left column): info items + attachments. */
  children?: ReactNode;
  /** Optional inline preview panel (DocumentQuickPreview). */
  previewPanel?: ReactNode;
  /** Footer: action buttons. */
  footer?: ReactNode;
  /** Extra right-slot content for the header (e.g. Chip + Edit button). */
  headerRightSlot?: ReactNode;
}

/**
 * Standard layout for document/file viewer pages:
 * Card-style shell + RequestDetailsHeader + two-column body (info | preview) + footer.
 *
 * Distinct from RequestDetailLayout (which has a resizable right sidebar).
 * Used by: document-detail, ViewDocument, file-details.
 */
export const DocumentDetailLayout = ({
  title: titleProp,
  status,
  onBack,
  isLoading,
  children,
  previewPanel,
  footer,
  headerRightSlot,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const title = titleProp ?? t("common:shared_ui.document_details");

  return (
    <div className="flex flex-col bg-white rounded-sm border border-border overflow-hidden">
      <RequestDetailsHeader
        title={title}
        status={headerRightSlot ? undefined : status}
        onBack={onBack}
        rightSlot={headerRightSlot}
        className="rounded-none"
      />

      {isLoading ? (
        <div className="flex items-center justify-center min-h-96">
          <Loader className="animate-spin text-primary" size={22} />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap justify-between gap-12 p-6 flex-1 overflow-auto">
            <div className="grow xl:max-w-[calc(100%-500px-24px)]">
              {children}
            </div>
            {previewPanel}
          </div>

          {footer && (
            <div className="flex justify-end gap-2 px-6 py-4 border-t mt-auto">
              {footer}
            </div>
          )}
        </>
      )}
    </div>
  );
};
